import type { SavedObjectiveSelect } from 'types';
import type { SavedObjectivesManager } from '@/state/experiments/useSavedObjectives';
import { useState } from 'react';
import { AlertTriangle, Bookmark, Pencil, Search, Trash2 } from 'lucide-react';
import { cn } from 'helpers/cn';
import {
  diffObjectives,
  flattenObjectives,
  isSameObjectives,
  OBJECTIVES_LOCKED_MESSAGE,
} from 'helpers/objectives-identity';
import { ConfirmDialog } from '@/components/Dialogs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  MAX_SAVED_OBJECTIVE_NAME_LENGTH,
  filterSavedObjectives,
  savedObjectivesNameError,
} from '@/state/experiments/saved-objectives-summary';
import { formatUpdated } from '@/state/experiments/saved-targeting-summary';
import { ConditionChips } from '../SavedTargeting/ConditionChips';
import { ObjectivesDiffRows } from './ObjectivesDiffRows';

type LoadSavedObjectivesDialogProps = { manager: SavedObjectivesManager; preselectedId: number | null };

const CHIP_LIMIT = 6;

/** The primary chip says it is the primary; the secondaries follow it in order. */
const entryChips = (entry: SavedObjectiveSelect) =>
  flattenObjectives(entry.rules).map(({ role, key, label }) => ({
    key,
    label: role === 'primary' ? `Primary: ${label}` : label,
  }));

/**
 * Pick saved objectives and load them, replacing the experiment's. Once a row is picked and the
 * card is not empty, the amber box shows exactly what goes and what comes; that box is the confirm
 * step. The same list renames and deletes entries in place. Mounted only while open.
 */
export const LoadSavedObjectivesDialog = ({ manager, preselectedId }: LoadSavedObjectivesDialogProps) => {
  const { savedObjectives, isLoading, currentRules, isEmpty, source, isLocked } = manager;
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState<number | null>(preselectedId);
  const [renamingId, setRenamingId] = useState<number | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const visible = filterSavedObjectives(savedObjectives, query);
  const selected = savedObjectives.find((entry) => entry.id === selectedId);
  const diff = selected && !isEmpty ? diffObjectives(currentRules, selected.rules) : null;
  const identical = !!selected && isSameObjectives(currentRules, selected.rules);
  const renameError = savedObjectivesNameError({
    name: renameValue,
    list: savedObjectives,
    exceptId: renamingId ?? -1,
  });

  const startRename = (entry: SavedObjectiveSelect) => {
    setDeletingId(null);
    setRenamingId(entry.id);
    setRenameValue(entry.name);
  };

  const saveRename = async () => {
    if (renamingId === null || !renameValue.trim() || renameError) return;
    if (await manager.rename({ id: renamingId, name: renameValue })) setRenamingId(null);
  };

  const confirmDelete = async (id: number) => {
    if (!(await manager.remove(id))) return;
    setDeletingId(null);
    if (selectedId === id) setSelectedId(null);
  };

  if (!isLoading && savedObjectives.length === 0) {
    return (
      <ConfirmDialog
        open
        title="Load saved objectives"
        onConfirm={() => manager.openSave('new')}
        onCancel={manager.closeDialog}
        confirmLabel="Save current objectives"
        confirmVariant="outline"
      >
        <div className="flex flex-col items-center gap-3 py-6 text-center" data-first-run>
          <span className="bg-brand-muted text-brand flex size-12 items-center justify-center rounded-xl">
            <Bookmark className="size-5" strokeWidth={1.5} />
          </span>
          <p className="text-foreground text-title font-medium">No saved objectives on this website yet</p>
          <p className="supporting max-w-sm">
            Save an experiment's objectives once, then load them on any other experiment here.
          </p>
        </div>
      </ConfirmDialog>
    );
  }

  return (
    <ConfirmDialog
      open
      title="Load saved objectives"
      description="Replaces this experiment's primary and secondary objectives with a copy of saved ones."
      onConfirm={() => (selected && !identical && !isLocked ? manager.load(selected).then(() => undefined) : undefined)}
      onCancel={manager.closeDialog}
      disabled={!selected || identical || isLocked}
      disabledReason={
        isLocked
          ? OBJECTIVES_LOCKED_MESSAGE
          : identical
            ? 'Already matches the current objectives.'
            : 'Pick saved objectives.'
      }
      confirmLabel={isEmpty ? 'Load' : 'Load and replace'}
    >
      <div className="flex flex-col gap-3">
        <div className="relative">
          <Search className="text-dim pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
          <Input
            value={query}
            onChange={(ev) => setQuery(ev.target.value)}
            placeholder="Search by name or event"
            aria-label="Search saved objectives"
            className="border-line bg-field text-foreground h-10 rounded-lg pl-9"
          />
        </div>

        {isLoading && savedObjectives.length === 0 ? <p className="supporting">Loading…</p> : null}
        {!isLoading && visible.length === 0 ? <p className="supporting">Nothing matches "{query.trim()}".</p> : null}

        {visible.length ? (
          <div
            role="radiogroup"
            aria-label="Saved objectives"
            className="border-line max-h-[22rem] overflow-y-auto rounded-lg border"
          >
            {visible.map((entry) => {
              const isSelected = entry.id === selectedId;
              const isRenaming = entry.id === renamingId;

              if (entry.id === deletingId) {
                return (
                  <div
                    key={entry.id}
                    className="border-line bg-danger-bg flex flex-wrap items-center gap-3 border-b px-3 py-3 last:border-b-0"
                  >
                    <p className="text-foreground min-w-0 flex-1 text-body">
                      Delete "{entry.name}"? Experiments that loaded it are not affected.
                    </p>
                    <Button variant="outline" size="sm" onClick={() => setDeletingId(null)} className="rounded-lg">
                      Cancel
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => void confirmDelete(entry.id)}
                      className="rounded-lg"
                    >
                      Delete
                    </Button>
                  </div>
                );
              }

              return (
                <div
                  key={entry.id}
                  className={cn(
                    'border-line group flex items-start gap-3 border-b px-3 py-3 last:border-b-0',
                    isSelected && 'bg-brand-muted shadow-[inset_3px_0_0_var(--color-brand)]',
                  )}
                >
                  <input
                    type="radio"
                    name="saved-objectives-load"
                    id={`saved-objectives-${entry.id}`}
                    checked={isSelected}
                    onChange={() => setSelectedId(entry.id)}
                    className="accent-brand mt-1 size-4 shrink-0"
                  />

                  <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                    {isRenaming ? (
                      <div className="flex flex-col gap-1">
                        <Input
                          value={renameValue}
                          maxLength={MAX_SAVED_OBJECTIVE_NAME_LENGTH + 20}
                          onChange={(ev) => setRenameValue(ev.target.value)}
                          onKeyDown={(ev) => {
                            if (ev.key === 'Enter') {
                              ev.preventDefault();
                              void saveRename();
                            }
                            if (ev.key === 'Escape') {
                              // Esc cancels the rename, not the whole dialog.
                              ev.preventDefault();
                              ev.stopPropagation();
                              setRenamingId(null);
                            }
                          }}
                          aria-label={`New name for ${entry.name}`}
                          aria-invalid={!!renameError}
                          className="border-line bg-field text-foreground h-9 rounded-lg"
                          autoFocus
                        />
                        {renameError ? <p className="text-danger text-micro">{renameError}</p> : null}
                      </div>
                    ) : (
                      <label
                        htmlFor={`saved-objectives-${entry.id}`}
                        className="flex cursor-pointer items-baseline gap-2"
                      >
                        <span className="text-foreground truncate text-body font-medium">{entry.name}</span>
                        {entry.id === source?.id ? <span className="supporting shrink-0">Current source</span> : null}
                      </label>
                    )}
                    <ConditionChips conditions={entryChips(entry)} limit={CHIP_LIMIT} />
                  </div>

                  <div className="flex shrink-0 items-center gap-1 self-center">
                    <span
                      className={cn(
                        'supporting group-focus-within:hidden group-hover:hidden',
                        (isSelected || isRenaming) && 'hidden',
                      )}
                    >
                      {formatUpdated(entry.updatedAt)}
                    </span>
                    <span
                      className={cn(
                        'hidden items-center gap-1 group-focus-within:flex group-hover:flex',
                        (isSelected || isRenaming) && 'flex',
                      )}
                    >
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-10 rounded-lg"
                        aria-label={`Rename ${entry.name}`}
                        onClick={() => startRename(entry)}
                      >
                        <Pencil className="size-4" strokeWidth={1.5} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="hover:text-danger size-10 rounded-lg"
                        aria-label={`Delete ${entry.name}`}
                        onClick={() => {
                          setRenamingId(null);
                          setDeletingId(entry.id);
                        }}
                      >
                        <Trash2 className="size-4" strokeWidth={1.5} />
                      </Button>
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        ) : null}

        <div aria-live="polite">
          {diff ? (
            <div className="border-warn-line bg-warn-bg flex flex-col gap-2 rounded-lg border p-3">
              <p className="text-warn flex items-center gap-2 text-body font-medium">
                <AlertTriangle className="size-4" strokeWidth={1.5} aria-hidden="true" />
                {identical ? 'Already matches the current objectives.' : 'This replaces the current objectives'}
              </p>
              {identical ? null : <ObjectivesDiffRows diff={diff} showKeeps={false} />}
            </div>
          ) : null}
        </div>

        <p className="supporting">
          {isLocked
            ? 'This experiment is running, so its objectives are locked. You can still rename and delete saved objectives here.'
            : 'You can undo right after loading.'}
        </p>
      </div>
    </ConfirmDialog>
  );
};
